import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Drawer
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RemoveIcon from '@mui/icons-material/Remove';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import API_CONFIG from '../config/api';
import { apiFetch } from '../api/client';
import DashboardInteligente from '../components/avaliacao/DashboardInteligente';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

const CHECKLIST_DOCS_FISCAL = [
  { id: 'relatorio-vistoria', nome: 'Relatório de Vistoria' },
  { id: 'parecer-tecnico', nome: 'Parecer Técnico do Fiscal' },
  { id: 'registro-ocorrencias', nome: 'Registro de Ocorrências' }
];

const CHECKLIST_DOCS_CONTRATOS = [
  { id: 'cnd', nome: 'CND' },
  { id: 'nota-fiscal', nome: 'Nota Fiscal' },
  { id: 'termo-designacao', nome: 'Termo de Designação' },
  { id: 'contrato-aditivo', nome: 'Contrato / Aditivo' }
];

const STATUS_DOC_CONFIG = {
  pendente: { label: 'Pendente', color: 'error' },
  encaminhado: { label: 'Encaminhado', color: 'warning' },
  validado: { label: 'Validado', color: 'success' }
};

const CRITERIOS_AVALIACAO_FISCAL = [
  { key: 'rigor', label: 'Rigor nas vistorias' },
  { key: 'prazo', label: 'Prazo de entrega de documentos' },
  { key: 'comunicacao', label: 'Comunicação' },
  { key: 'resolucao', label: 'Resolução de conflitos' },
  { key: 'assiduidade', label: 'Assiduidade' },
  { key: 'alinhamento', label: 'Alinhamento estratégico' }
];

function getNotaColorGlobal(nota) {
  if (nota >= 1 && nota < 2) return 'error';
  if (nota >= 2 && nota < 3) return 'warning';
  if (nota >= 3 && nota < 4) return 'info';
  if (nota >= 4) return 'success';
  return 'default';
}

function calcularMediaFiscal(avaliacaoFiscal) {
  const vals = CRITERIOS_AVALIACAO_FISCAL.map((c) => avaliacaoFiscal[c.key]).filter(
    (v) => typeof v === 'number'
  );
  if (!vals.length) return 0;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

const AVALIACAO_FISCAL_INICIAL = {
  rigor: 3,
  prazo: 3,
  comunicacao: 3,
  resolucao: 3,
  assiduidade: 3,
  alinhamento: 3,
  pontosPositivos: '',
  pontosMelhorar: '',
  observacoes: ''
};

/** Painel lateral 360º do Fiscal — módulo extraído para modularização gradual. */
function DrawerPainelFiscal({
  fiscal,
  prestadoresDoFiscal,
  open,
  onClose,
  formatCurrency,
  formatDate,
  getNotaColor,
  calcularDiasVencimento,
  calcularNovaVisita,
  getAvaliacoesContrato,
  canAddAvaliacao,
  isCargoCorporativo,
  onAvaliar,
  onEditarAvaliacao,
  onExcluirAvaliacao,
  onGerarPdf,
  onVerPdf
}) {
  const [abaAtiva, setAbaAtiva] = useState(0);
  const [statusDocumentos, setStatusDocumentos] = useState({});
  const [avaliacaoFiscal, setAvaliacaoFiscal] = useState({ ...AVALIACAO_FISCAL_INICIAL });
  const [historicoAcoes, setHistoricoAcoes] = useState([
    { id: 1, data: '05/07/2026', acao: 'Relatório de Vistoria encaminhado', contrato: '011/2023', tipo: 'documento' },
    { id: 2, data: '28/06/2026', acao: 'CND validada pelo Setor de Contratos', contrato: '008/2024', tipo: 'validacao' },
    { id: 3, data: '15/06/2026', acao: 'Avaliação 360º registrada', contrato: '-', tipo: 'avaliacao' }
  ]);

  useEffect(() => {
    if (!open) return;
    setAbaAtiva(0);
  }, [open, fiscal?.nome]);

  const getStatusDoc = (contratoId, docId) =>
    statusDocumentos[`${contratoId}-${docId}`] || 'pendente';

  const ciclarStatusDoc = (contratoId, docId) => {
    const key = `${contratoId}-${docId}`;
    const ordem = ['pendente', 'encaminhado', 'validado'];
    const atual = getStatusDoc(contratoId, docId);
    const proximo = ordem[(ordem.indexOf(atual) + 1) % ordem.length];
    setStatusDocumentos((prev) => ({ ...prev, [key]: proximo }));
    setHistoricoAcoes((prev) => [
      {
        id: Date.now(),
        data: new Date().toLocaleDateString('pt-BR'),
        acao: `Status alterado para "${STATUS_DOC_CONFIG[proximo].label}"`,
        contrato: contratoId,
        tipo: 'documento'
      },
      ...prev
    ]);
  };

  const handleSimularUpload = (contratoId, docNome) => {
    setHistoricoAcoes((prev) => [
      {
        id: Date.now(),
        data: new Date().toLocaleDateString('pt-BR'),
        acao: `Documento anexado: ${docNome}`,
        contrato: contratoId,
        tipo: 'upload'
      },
      ...prev
    ]);
  };

  const handleSalvarAvaliacaoFiscal = () => {
    const media = calcularMediaFiscal(avaliacaoFiscal);
    setHistoricoAcoes((prev) => [
      {
        id: Date.now(),
        data: new Date().toLocaleDateString('pt-BR'),
        acao: `Avaliação 360º salva — nota média ${media}`,
        contrato: '-',
        tipo: 'avaliacao'
      },
      ...prev
    ]);
  };

  const mediaFiscal = calcularMediaFiscal(avaliacaoFiscal);

  const renderChecklist = (contrato, docs, titulo, corTitulo) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color={corTitulo} sx={{ mb: 1 }}>
        {titulo}
      </Typography>
      <List dense disablePadding>
        {docs.map((doc) => {
          const status = getStatusDoc(contrato.numeroContrato, doc.id);
          const cfg = STATUS_DOC_CONFIG[status];
          return (
            <ListItem
              key={doc.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 1,
                bgcolor: 'background.paper',
                boxShadow: 1
              }}
              secondaryAction={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title="Simular anexo">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSimularUpload(contrato.numeroContrato, doc.nome)}
                    >
                      <FileUploadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Chip
                    label={cfg.label}
                    color={cfg.color}
                    size="small"
                    onClick={() => ciclarStatusDoc(contrato.numeroContrato, doc.id)}
                    sx={{ cursor: 'pointer', minWidth: 100 }}
                  />
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <DescriptionIcon color="action" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={doc.nome} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 480, md: 560 }, maxWidth: '100vw' }
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <PersonIcon />
            <Typography variant="overline" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
              Dossiê 360º — Fiscal de Contratos
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {fiscal?.nome || 'Fiscal'}
          </Typography>
          {fiscal?.setor && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Setor: {fiscal.setor} {fiscal.email ? `• ${fiscal.email}` : ''}
            </Typography>
          )}
          <Chip
            label={`${prestadoresDoFiscal?.length || 0} contrato(s) sob responsabilidade`}
            size="small"
            sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Fechar painel">
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={abaAtiva}
        onChange={(_, v) => setAbaAtiva(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
      >
        <Tab label="Contratos e Documentos" sx={{ fontSize: '0.75rem', minHeight: 48 }} />
        <Tab label="Avaliação 360º" sx={{ fontSize: '0.75rem', minHeight: 48 }} />
        <Tab label="Histórico" sx={{ fontSize: '0.75rem', minHeight: 48 }} />
      </Tabs>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, maxHeight: 'calc(100vh - 180px)' }}>
        {abaAtiva === 0 && (
          <Box>
            {!prestadoresDoFiscal?.length ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhum contrato vinculado a este fiscal na unidade atual.
              </Alert>
            ) : (
              prestadoresDoFiscal.map((contrato) => {
                const diasVencimento = calcularDiasVencimento(contrato.vigenciaFim);
                const novaVisita = calcularNovaVisita(contrato.vigenciaFim);
                const avaliacoesContrato = getAvaliacoesContrato(contrato.numeroContrato);

                return (
                <Card key={contrato.id} sx={{ mb: 2, borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                      Contrato {contrato.numeroContrato}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {contrato.empresa} • {contrato.setor}
                    </Typography>

                    <Grid
                      container
                      spacing={1.5}
                      sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}
                    >
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Valor/Mês</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(contrato.valorMes)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Vigência Início</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(contrato.vigenciaInicio)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Vigência Fim</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(contrato.vigenciaFim)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Dias p/ Vencimento</Typography>
                        <Chip
                          label={diasVencimento === '-' ? '-' : `${diasVencimento} dias`}
                          size="small"
                          color={
                            diasVencimento === '-' ? 'default' :
                            diasVencimento <= 30 ? 'error' :
                            diasVencimento <= 60 ? 'warning' : 'success'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Última Visita</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(contrato.dataUltimaVisita)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Nova Visita</Typography>
                        <Typography variant="body2" fontWeight={600}>{novaVisita}</Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Avaliações do Fornecedor
                    </Typography>
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                      {avaliacoesContrato.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                          {avaliacoesContrato.map((avaliacao) => (
                            <Chip
                              key={avaliacao.id}
                              label={`${avaliacao.nota}★`}
                              size="small"
                              color={getNotaColor(avaliacao.nota)}
                              icon={<PictureAsPdfIcon />}
                              onClick={() => onVerPdf(avaliacao)}
                              sx={{
                                cursor: avaliacao.pdfPath ? 'pointer' : 'default',
                                borderRadius: 2
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Nenhuma avaliação registrada.
                        </Typography>
                      )}

                      {!isCargoCorporativo && (
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                          {canAddAvaliacao(contrato.setor) && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => onAvaliar(contrato)}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                              }}
                            >
                              Avaliar
                            </Button>
                          )}
                          {avaliacoesContrato.map((avaliacao) => (
                            <Box key={avaliacao.id} display="flex" gap={0.5}>
                              <Tooltip title="Gerar PDF">
                                <IconButton size="small" color="error" onClick={() => onGerarPdf(avaliacao)}>
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton size="small" color="primary" onClick={() => onEditarAvaliacao(avaliacao)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton size="small" color="error" onClick={() => onExcluirAvaliacao(avaliacao)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {renderChecklist(
                      contrato,
                      CHECKLIST_DOCS_FISCAL,
                      'Responsabilidade do Fiscal',
                      'primary.main'
                    )}
                    {renderChecklist(
                      contrato,
                      CHECKLIST_DOCS_CONTRATOS,
                      'Responsabilidade do Setor de Contratos',
                      'secondary.main'
                    )}
                  </CardContent>
                </Card>
                );
              })
            )}
          </Box>
        )}

        {abaAtiva === 1 && (
          <Box>
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom fontWeight={600}>
                  Avaliação do Fiscal (360º)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Critérios avaliados pelo Setor de Contratos. Nota de 1 (Ruim) a 5 (Excelente).
                </Typography>
                {CRITERIOS_AVALIACAO_FISCAL.map((criterio, index) => (
                  <Box
                    key={criterio.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: 'white'
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 24 }}>
                      {index + 1}.
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {criterio.label}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Nota</InputLabel>
                      <Select
                        value={avaliacaoFiscal[criterio.key]}
                        label="Nota"
                        onChange={(e) =>
                          setAvaliacaoFiscal((prev) => ({
                            ...prev,
                            [criterio.key]: Number(e.target.value)
                          }))
                        }
                      >
                        <MenuItem value={1}>1 - Ruim</MenuItem>
                        <MenuItem value={2}>2 - Regular</MenuItem>
                        <MenuItem value={3}>3 - Bom</MenuItem>
                        <MenuItem value={4}>4 - Ótimo</MenuItem>
                        <MenuItem value={5}>5 - Excelente</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ))}
                <TextField
                  label="Pontos Positivos / Elogios"
                  multiline
                  rows={3}
                  fullWidth
                  value={avaliacaoFiscal.pontosPositivos}
                  onChange={(e) =>
                    setAvaliacaoFiscal((prev) => ({ ...prev, pontosPositivos: e.target.value }))
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  label="Pontos a Melhorar"
                  multiline
                  rows={3}
                  fullWidth
                  value={avaliacaoFiscal.pontosMelhorar}
                  onChange={(e) =>
                    setAvaliacaoFiscal((prev) => ({ ...prev, pontosMelhorar: e.target.value }))
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  label="Observações gerais"
                  multiline
                  rows={2}
                  fullWidth
                  value={avaliacaoFiscal.observacoes}
                  onChange={(e) =>
                    setAvaliacaoFiscal((prev) => ({ ...prev, observacoes: e.target.value }))
                  }
                  sx={{ mt: 2 }}
                />

                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 3,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(66,165,245,0.12) 100%)',
                    border: '2px solid',
                    borderColor: `${getNotaColorGlobal(mediaFiscal)}.light`
                  }}
                >
                  <AssessmentIcon color={getNotaColorGlobal(mediaFiscal)} sx={{ fontSize: 36, mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Nota Média 360º (calculada automaticamente)
                  </Typography>
                  <Chip
                    label={mediaFiscal}
                    color={getNotaColorGlobal(mediaFiscal)}
                    sx={{
                      mt: 1,
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      height: 48,
                      px: 2,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    onClick={handleSalvarAvaliacaoFiscal}
                    sx={{
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                    }}
                  >
                    Salvar Avaliação 360º
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {abaAtiva === 2 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Histórico de Ações
            </Typography>
            <List disablePadding>
              {historicoAcoes.map((item, idx) => (
                <React.Fragment key={item.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      {item.tipo === 'validacao' ? (
                        <CheckCircleIcon color="success" />
                      ) : item.tipo === 'upload' ? (
                        <FileUploadIcon color="primary" />
                      ) : item.tipo === 'avaliacao' ? (
                        <AssessmentIcon color="info" />
                      ) : (
                        <HistoryIcon color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.acao}
                      secondary={`${item.data}${item.contrato !== '-' ? ` • Contrato ${item.contrato}` : ''}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  {idx < historicoAcoes.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default function AvaliacaoFornecedoresPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandled = useRef(false);
  const { isCargoCorporativo, canAddAvaliacao, unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const visaoFiscal = isFiscalSetorial(user, rbacRole || getUserRole(user));
  const setorFiscal = getFiscalSetor(user, fiscalSetor);
  const scopeOpts = { user, rbacRole: rbacRole || getUserRole(user), fiscalSetor };
  const [prestadores, setPrestadores] = useState([]);
  const [avaliacoesTodas, setAvaliacoesTodas] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [fiscais, setFiscais] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    numeroContrato: '',
    empresa: '',
    setor: '',
    responsavel: '',
    objeto: '',
    aditivo: '',
    // 8 critérios de avaliação
    criterio1: 3, // Serviço prestado conforme o Contrato
    criterio2: 3, // Serviços prestados de acordo com a rotina/programação de execução
    criterio3: 3, // Qualidade na entrega do serviço/produto
    criterio4: 3, // Comunicação acessível e ágil
    criterio5: 3, // Empenho na resolução de problemas sinalizados na prestação do serviço
    criterio6: 3, // Confiabilidade e Entrega
    criterio7: 3, // Segurança na Execução do Serviço
    criterio8: 3, // Alinhamento estratégico de acordo com as diretrizes do hospital
    nota: 3, // Calculada automaticamente
    pontosPositivos: '',
    pontosNegativos: '',
    dataAvaliacao: new Date().toISOString().split('T')[0],
    pdfFile: null
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [editingAvaliacao, setEditingAvaliacao] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [indicadoresModalOpen, setIndicadoresModalOpen] = useState(false);
  const [aditivos, setAditivos] = useState([]);
  const [planoAcaoModalOpen, setPlanoAcaoModalOpen] = useState(false);
  const [criteriosExtras, setCriteriosExtras] = useState([]);
  const [indicadoresTab, setIndicadoresTab] = useState(0); // Controle das abas de indicadores
  const [planosAcao, setPlanosAcao] = useState([]);
  const [planoAcaoForm, setPlanoAcaoForm] = useState({
    avaliacaoId: null,
    numeroContrato: '',
    empresa: '',
    dataInicio: new Date().toISOString().split('T')[0],
    dataConclusao: '',
    problemasIdentificados: '',
    acoesCorretivas: '',
    responsavelAcompanhamento: '',
    observacoes: ''
  });

  // Estados para filtros avançados
  const [filtroAvancadoOpen, setFiltroAvancadoOpen] = useState(false);
  const [filtros, setFiltros] = useState({
    setor: '',
    empresa: '',
    numeroContrato: '',
    statusAvaliacao: '', // 'avaliados', 'naoAvaliados', 'todos'
    assinaturaInicio: '',
    assinaturaFim: '',
    vigenciaInicioInicio: '',
    vigenciaInicioFim: '',
    vigenciaFimInicio: '',
    vigenciaFimFim: ''
  });
  const [prestadoresFiltrados, setPrestadoresFiltrados] = useState([]);

  // Painel 360º do Fiscal
  const [fiscalPanelOpen, setFiscalPanelOpen] = useState(false);
  const [fiscalPanelData, setFiscalPanelData] = useState({ fiscal: null, prestadores: [] });

  const fetchPrestadores = async () => {
    setLoading(true);
    try {
      // TODO Backend/LGPD Fase 5: ?setor= isola empresas do setor do FISCAL
      let url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/prestadores?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/prestadores`;
      url = withSetorScope(url, scopeOpts);
      
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar prestadores');
      let data = await res.json();
      if (visaoFiscal && setorFiscal) {
        data = (Array.isArray(data) ? data : []).filter((p) => p.setor === setorFiscal);
      }
      setPrestadores(data);
    } catch (error) {
      console.error('Erro ao buscar prestadores:', error);
      setPrestadores([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvaliacoes = async () => {
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      // TODO Backend: GET /api/avaliacoes deve filtrar por ?setor=
      if (visaoFiscal && setorFiscal) params.set('setor', setorFiscal);
      const qs = params.toString();
      const url = qs ? `${BACKEND_URL}/api/avaliacoes?${qs}` : `${BACKEND_URL}/api/avaliacoes`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar avaliações');
      const data = await res.json();
      setAvaliacoesTodas(data);
    } catch (error) {
      console.error('Erro ao buscar avaliações:', error);
      setAvaliacoes([]);
      setAvaliacoesTodas([]);
    }
  };

  const fetchFiscais = async () => {
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      if (visaoFiscal && setorFiscal) params.set('setor', setorFiscal);
      const qs = params.toString();
      const url = qs ? `${BACKEND_URL}/api/fiscais?${qs}` : `${BACKEND_URL}/api/fiscais`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar fiscais');
      const data = await res.json();
      setFiscais(data);
    } catch (error) {
      console.error('Erro ao buscar fiscais:', error);
      setFiscais([]);
    }
  };

  const fetchAditivos = async (numeroContrato) => {
    if (!numeroContrato) return;
    try {
      const res = await apiFetch(
        `/api/aditivos?numeroContrato=${encodeURIComponent(numeroContrato)}${
          unidadeSelecionada?.codigo ? `&unidade=${encodeURIComponent(unidadeSelecionada.codigo)}` : ''
        }`
      );
      if (!res.ok) throw new Error('Erro ao buscar aditivos');
      const data = await res.json();
      setAditivos(data);
    } catch (error) {
      console.error('Erro ao buscar aditivos:', error);
      setAditivos([]);
    }
  };

  // Função para calcular a nota final automaticamente
  const calcularNotaFinal = () => {
    const criterios = [
      form.criterio1, form.criterio2, form.criterio3, form.criterio4,
      form.criterio5, form.criterio6, form.criterio7, form.criterio8
    ];
    
    // Adicionar critérios extras
    criteriosExtras.forEach(criterio => {
      criterios.push(criterio.nota || 3);
    });
    
    const soma = criterios.reduce((total, nota) => total + nota, 0);
    const totalCriterios = 8 + criteriosExtras.length;
    return Math.round((soma / totalCriterios) * 10) / 10; // Arredondar para 1 casa decimal
  };

  // Função para obter a classificação da nota
  const getClassificacaoNota = (nota) => {
    if (nota >= 4.1) return { nivel: 'Excelente', cor: 'success' };
    if (nota >= 3.1) return { nivel: 'Ótimo', cor: 'info' };
    if (nota >= 2.1) return { nivel: 'Bom', cor: 'primary' };
    if (nota >= 1.1) return { nivel: 'Regular', cor: 'warning' };
    return { nivel: 'Ruim', cor: 'error' };
  };

  // Função para adicionar critério extra
  const adicionarCriterioExtra = () => {
    const novoCriterio = {
      id: Date.now(), // ID único baseado no timestamp
      descricao: '',
      nota: 3
    };
    setCriteriosExtras([...criteriosExtras, novoCriterio]);
  };

  // Função para remover critério extra
  const removerCriterioExtra = (id) => {
    setCriteriosExtras(criteriosExtras.filter(criterio => criterio.id !== id));
  };

  // Função para atualizar critério extra
  const atualizarCriterioExtra = (id, campo, valor) => {
    setCriteriosExtras(criteriosExtras.map(criterio => 
      criterio.id === id ? { ...criterio, [campo]: valor } : criterio
    ));
  };

  const fetchPlanosAcao = async () => {
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const url = qs ? `${BACKEND_URL}/api/planos-acao?${qs}` : `${BACKEND_URL}/api/planos-acao`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar planos de ação');
      const data = await res.json();
      setPlanosAcao(data);
    } catch (error) {
      console.error('Erro ao buscar planos de ação:', error);
    }
  };

  // Função para buscar contratos em atraso por setor
  const obterContratosEmAtraso = (setor) => {
    const hoje = new Date();
    
    const contratosAtraso = prestadores.filter(prestador => {
      if (!prestador.setor || prestador.setor !== setor) return false;
      
      // Verificar se tem avaliação
      const temAvaliacao = avaliacoes.some(av => av.empresa === prestador.empresa);
      if (temAvaliacao) {
        return false; // Se tem avaliação, não está em atraso
      }
      
      // Se não tem avaliação, verificar se está próximo do vencimento
      if (prestador.vigenciaFim) {
        let vencimento;
        if (prestador.vigenciaFim.includes('/')) {
          const [day, month, year] = prestador.vigenciaFim.split('/');
          vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
          vencimento = new Date(prestador.vigenciaFim);
        }
        
        if (!isNaN(vencimento.getTime())) {
          const diasParaVencimento = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
          return diasParaVencimento <= 60; // Mesma lógica dos cards
        }
      }
      
      return false;
    });

    return contratosAtraso.map(contrato => {
      let diasAtraso = 0;
      let motivoAtraso = '';
      
      if (contrato.vigenciaFim) {
        let vencimento;
        if (contrato.vigenciaFim.includes('/')) {
          const [day, month, year] = contrato.vigenciaFim.split('/');
          vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
          vencimento = new Date(contrato.vigenciaFim);
        }
        
        if (!isNaN(vencimento.getTime())) {
          const diasParaVencimento = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
          if (diasParaVencimento <= 0) {
            diasAtraso = Math.abs(diasParaVencimento);
            motivoAtraso = `Vencido há ${diasAtraso} dias`;
          } else {
            diasAtraso = diasParaVencimento;
            motivoAtraso = `Vence em ${diasParaVencimento} dias`;
          }
        }
      }
      
      return {
        numero: contrato.numeroContrato,
        empresa: contrato.empresa,
        diasAtraso: diasAtraso,
        motivoAtraso: motivoAtraso
      };
    });
  };

  // Funções para Dashboard de Avaliações
  const calcularEstatisticasAvaliacoes = () => {
    if (avaliacoes.length === 0) return null;

    const estatisticas = {
      total: avaliacoes.length,
      mediaGeral: 0,
      distribuicao: { excelente: 0, otimo: 0, bom: 0, regular: 0, ruim: 0 },
      tendencia: [],
      porEmpresa: {},
      porSetor: {},
      recentes: []
    };

    // Calcular média geral e distribuição
    let somaTotal = 0;
    avaliacoes.forEach(avaliacao => {
      somaTotal += avaliacao.nota;
      
      // Distribuição por classificação
      if (avaliacao.nota >= 4.1) estatisticas.distribuicao.excelente++;
      else if (avaliacao.nota >= 3.1) estatisticas.distribuicao.otimo++;
      else if (avaliacao.nota >= 2.1) estatisticas.distribuicao.bom++;
      else if (avaliacao.nota >= 1.1) estatisticas.distribuicao.regular++;
      else estatisticas.distribuicao.ruim++;

      // Por empresa
      if (!estatisticas.porEmpresa[avaliacao.empresa]) {
        estatisticas.porEmpresa[avaliacao.empresa] = {
          total: 0,
          soma: 0,
          media: 0,
          avaliacoes: []
        };
      }
      estatisticas.porEmpresa[avaliacao.empresa].total++;
      estatisticas.porEmpresa[avaliacao.empresa].soma += avaliacao.nota;
      estatisticas.porEmpresa[avaliacao.empresa].avaliacoes.push(avaliacao);

      // Por setor (se disponível)
      if (avaliacao.setor) {
        if (!estatisticas.porSetor[avaliacao.setor]) {
          estatisticas.porSetor[avaliacao.setor] = {
            total: 0,
            soma: 0,
            media: 0
          };
        }
        estatisticas.porSetor[avaliacao.setor].total++;
        estatisticas.porSetor[avaliacao.setor].soma += avaliacao.nota;
      }
    });

    estatisticas.mediaGeral = Math.round((somaTotal / avaliacoes.length) * 10) / 10;

    // Calcular médias por empresa e setor
    Object.keys(estatisticas.porEmpresa).forEach(empresa => {
      const dados = estatisticas.porEmpresa[empresa];
      dados.media = Math.round((dados.soma / dados.total) * 10) / 10;
    });

    Object.keys(estatisticas.porSetor).forEach(setor => {
      const dados = estatisticas.porSetor[setor];
      dados.media = Math.round((dados.soma / dados.total) * 10) / 10;
    });

    // Avaliações recentes (últimas 5)
    estatisticas.recentes = [...avaliacoes]
      .sort((a, b) => new Date(b.dataAvaliacao) - new Date(a.dataAvaliacao))
      .slice(0, 5);

    // Calcular tendência (últimos 6 meses)
    const agora = new Date();
    const seisMesesAtras = new Date(agora.getFullYear(), agora.getMonth() - 6, agora.getDate());
    
    estatisticas.tendencia = avaliacoes
      .filter(av => new Date(av.dataAvaliacao) >= seisMesesAtras)
      .sort((a, b) => new Date(a.dataAvaliacao) - new Date(b.dataAvaliacao))
      .reduce((acc, av) => {
        const mes = new Date(av.dataAvaliacao).toISOString().substr(0, 7); // YYYY-MM
        if (!acc[mes]) acc[mes] = { soma: 0, total: 0 };
        acc[mes].soma += av.nota;
        acc[mes].total++;
        return acc;
      }, {});

    // Converter tendência para array com médias
    estatisticas.tendencia = Object.keys(estatisticas.tendencia).map(mes => ({
      mes,
      media: Math.round((estatisticas.tendencia[mes].soma / estatisticas.tendencia[mes].total) * 10) / 10,
      total: estatisticas.tendencia[mes].total
    }));

    return estatisticas;
  };

  const obterCorPorNota = (nota) => {
    if (nota >= 4.1) return '#4caf50'; // Verde - Excelente
    if (nota >= 3.1) return '#2196f3'; // Azul - Ótimo
    if (nota >= 2.1) return '#ff9800'; // Laranja - Bom
    if (nota >= 1.1) return '#ff5722'; // Vermelho claro - Regular
    return '#f44336'; // Vermelho - Ruim
  };

  const obterClassificacaoPorNota = (nota) => {
    if (nota >= 4.1) return 'Excelente';
    if (nota >= 3.1) return 'Ótimo';
    if (nota >= 2.1) return 'Bom';
    if (nota >= 1.1) return 'Regular';
    return 'Ruim';
  };

  // Função para gerar PDF da avaliação
  const gerarPDFAvaliacao = async (avaliacao) => {
    try {
      setLoading(true);
      
      const response = await apiFetch('/api/gerar-pdf-avaliacao', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          avaliacaoId: avaliacao.id
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao gerar PDF');
      }

      // Criar blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avaliacao_${avaliacao.empresa}_${avaliacao.numeroContrato}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('PDF gerado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setError('Erro ao gerar PDF da avaliação');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar Excel
  const exportarExcelAvaliacoes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const exportUrl = qs ? `${BACKEND_URL}/api/exportar-excel-avaliacoes?${qs}` : `${BACKEND_URL}/api/exportar-excel-avaliacoes`;
      const response = await fetch(exportUrl);
      if (!response.ok) {
        throw new Error('Erro ao exportar Excel');
      }

      // Criar blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avaliacoes_fornecedores_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Excel exportado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      setError('Erro ao exportar Excel');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrestadores();
    fetchAvaliacoes();
    fetchFiscais();
    fetchPlanosAcao();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on unidadeSelecionada change only
  }, [unidadeSelecionada]);

  // Filtrar avaliações pela unidade selecionada
  useEffect(() => {
    if (unidadeSelecionada && prestadores.length > 0) {
      const numerosContratos = prestadores.map(p => p.numeroContrato);
      const avaliacoesFiltradas = avaliacoesTodas.filter(av => numerosContratos.includes(av.numeroContrato));
      setAvaliacoes(avaliacoesFiltradas);
    } else {
      setAvaliacoes(avaliacoesTodas);
    }
  }, [prestadores, avaliacoesTodas, unidadeSelecionada]);

  // Função auxiliar para detectar se é apenas ano e ajustar data
  const ajustarDataParaAno = (dataString, isFim = false) => {
    const data = new Date(dataString);
    
    // Se a string tem apenas 4 caracteres, é um ano
    if (dataString.length === 4) {
      const ano = parseInt(dataString);
      if (isFim) {
        // Para data fim, usar 31/12 do ano
        return new Date(ano, 11, 31, 23, 59, 59);
      } else {
        // Para data início, usar 01/01 do ano
        return new Date(ano, 0, 1, 0, 0, 0);
      }
    }
    
    // Se a string tem formato YYYY-MM (7 caracteres), é ano-mês
    if (dataString.length === 7 && dataString.includes('-')) {
      const [ano, mes] = dataString.split('-').map(Number);
      if (isFim) {
        // Para data fim, usar último dia do mês
        return new Date(ano, mes, 0, 23, 59, 59);
      } else {
        // Para data início, usar primeiro dia do mês
        return new Date(ano, mes - 1, 1, 0, 0, 0);
      }
    }
    
    return data;
  };

  // Função para aplicar filtros avançados
  const aplicarFiltros = useCallback(() => {
    let resultado = [...prestadores];

    // Filtro por setor
    if (filtros.setor) {
      resultado = resultado.filter(p => p.setor === filtros.setor);
    }

    // Filtro por empresa
    if (filtros.empresa) {
      resultado = resultado.filter(p => 
        p.empresa.toLowerCase().includes(filtros.empresa.toLowerCase())
      );
    }

    // Filtro por número do contrato
    if (filtros.numeroContrato) {
      resultado = resultado.filter(p => 
        p.numeroContrato.toLowerCase().includes(filtros.numeroContrato.toLowerCase())
      );
    }

    // Filtro por status de avaliação
    if (filtros.statusAvaliacao) {
      if (filtros.statusAvaliacao === 'avaliados') {
        resultado = resultado.filter(p => 
          avaliacoes.some(av => av.empresa === p.empresa)
        );
      } else if (filtros.statusAvaliacao === 'naoAvaliados') {
        resultado = resultado.filter(p => 
          !avaliacoes.some(av => av.empresa === p.empresa)
        );
      }
    }

    // Filtro por data de assinatura
    if (filtros.assinaturaInicio || filtros.assinaturaFim) {
      resultado = resultado.filter(p => {
        if (!p.assinatura) return false;
        
        const dataAssinatura = new Date(p.assinatura);
        if (isNaN(dataAssinatura.getTime())) return false;

        if (filtros.assinaturaInicio) {
          const dataInicio = ajustarDataParaAno(filtros.assinaturaInicio, false);
          if (dataAssinatura < dataInicio) return false;
        }

        if (filtros.assinaturaFim) {
          const dataFim = ajustarDataParaAno(filtros.assinaturaFim, true);
          if (dataAssinatura > dataFim) return false;
        }

        return true;
      });
    }

    // Filtro por início da vigência
    if (filtros.vigenciaInicioInicio || filtros.vigenciaInicioFim) {
      resultado = resultado.filter(p => {
        if (!p.vigenciaInicio) return false;
        
        const dataVigenciaInicio = new Date(p.vigenciaInicio);
        if (isNaN(dataVigenciaInicio.getTime())) return false;

        if (filtros.vigenciaInicioInicio) {
          const dataInicio = ajustarDataParaAno(filtros.vigenciaInicioInicio, false);
          if (dataVigenciaInicio < dataInicio) return false;
        }

        if (filtros.vigenciaInicioFim) {
          const dataFim = ajustarDataParaAno(filtros.vigenciaInicioFim, true);
          if (dataVigenciaInicio > dataFim) return false;
        }

        return true;
      });
    }

    // Filtro por fim da vigência
    if (filtros.vigenciaFimInicio || filtros.vigenciaFimFim) {
      resultado = resultado.filter(p => {
        if (!p.vigenciaFim) return false;
        
        const dataVigenciaFim = new Date(p.vigenciaFim);
        if (isNaN(dataVigenciaFim.getTime())) return false;

        if (filtros.vigenciaFimInicio) {
          const dataInicio = ajustarDataParaAno(filtros.vigenciaFimInicio, false);
          if (dataVigenciaFim < dataInicio) return false;
        }

        if (filtros.vigenciaFimFim) {
          const dataFim = ajustarDataParaAno(filtros.vigenciaFimFim, true);
          if (dataVigenciaFim > dataFim) return false;
        }

        return true;
      });
    }

    setPrestadoresFiltrados(resultado);
  }, [prestadores, filtros, avaliacoes]);

  // Aplicar filtros quando prestadores ou filtros mudarem
  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltros({
      setor: '',
      empresa: '',
      numeroContrato: '',
      statusAvaliacao: '',
      assinaturaInicio: '',
      assinaturaFim: '',
      vigenciaInicioInicio: '',
      vigenciaInicioFim: '',
      vigenciaFimInicio: '',
      vigenciaFimFim: ''
    });
  };

  // Função para verificar se há filtros ativos
  const temFiltrosAtivos = () => {
    return Object.values(filtros).some(valor => valor !== '');
  };

  // Função para obter o responsável do setor (apenas fiscais cadastrados na unidade)
  const getResponsavelSetor = (setor) => {
    const setorParaFiscal = {};
    fiscais.forEach(fiscal => {
      setorParaFiscal[fiscal.setor] = fiscal.nome;
    });
    return setorParaFiscal[setor] || 'Fiscal não cadastrado';
  };

  const getPrestadoresPorFiscal = (nomeFiscal) =>
    prestadoresFiltrados.filter((p) => getResponsavelSetor(p.setor) === nomeFiscal);

  const handleOpenFiscalPanel = (nomeFiscal, prestadoresDoFiscal) => {
    if (!nomeFiscal || nomeFiscal === 'Fiscal não cadastrado') return;
    const fiscalInfo =
      fiscais.find((f) => f.nome === nomeFiscal) || {
        nome: nomeFiscal,
        setor: prestadoresDoFiscal[0]?.setor || ''
      };
    setFiscalPanelData({ fiscal: fiscalInfo, prestadores: prestadoresDoFiscal });
    setFiscalPanelOpen(true);
  };

  // Função para calcular dias para vencimento
  const calcularDiasVencimento = (dataFim) => {
    if (!dataFim) return '-';
    
    let vencimento;
    
    // Se a data está no formato DD/MM/YYYY, converte para o formato correto
    if (dataFim.includes('/') && dataFim.split('/').length === 3) {
      const [day, month, year] = dataFim.split('/');
      if (day && month && year) {
        // Cria a data no formato YYYY-MM-DD para evitar inversão
        vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    } else {
      vencimento = new Date(dataFim);
    }
    
    if (isNaN(vencimento.getTime())) return '-';
    
    const hoje = new Date();
    const diffTime = vencimento - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Função para calcular data da nova visita (90 dias antes do vencimento)
  const calcularNovaVisita = (dataFim) => {
    if (!dataFim) return '-';
    
    let vencimento;
    
    // Se a data está no formato DD/MM/YYYY, converte para o formato correto
    if (dataFim.includes('/') && dataFim.split('/').length === 3) {
      const [day, month, year] = dataFim.split('/');
      if (day && month && year) {
        // Cria a data no formato YYYY-MM-DD para evitar inversão
        vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    } else {
      vencimento = new Date(dataFim);
    }
    
    if (isNaN(vencimento.getTime())) return '-';
    
    const novaVisita = new Date(vencimento);
    novaVisita.setDate(vencimento.getDate() - 90);
    return novaVisita.toLocaleDateString('pt-BR');
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Se a data está no formato DD/MM/YYYY, retorna como está
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString; // Já está no formato correto
    }
    
    // Se a data está no formato YYYY-MM-DD, converte para DD/MM/YYYY
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Fallback para o comportamento original
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Função para formatar moeda
  const formatCurrency = (valor) => {
    if (!valor) return 'R$ 0,00';
    const num = parseFloat(valor);
    if (isNaN(num)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num);
  };

  // Função para obter avaliações de um contrato
  const getAvaliacoesContrato = (numeroContrato) => {
    return avaliacoes.filter(av => av.numeroContrato === numeroContrato);
  };

  // Função para obter cor da nota
  const getNotaColor = (nota) => {
    if (nota >= 1 && nota < 2) return 'error';
    if (nota >= 2 && nota < 3) return 'warning';
    if (nota >= 3 && nota < 4) return 'info';
    if (nota >= 4 && nota < 5) return 'success';
    if (nota === 5) return 'success';
    return 'default';
  };

  const handleOpenModal = (prestador = null) => {
    if (prestador) {
      // Buscar aditivos do prestador
      fetchAditivos(prestador.numeroContrato);
      
      setForm({
        numeroContrato: prestador.numeroContrato,
        empresa: prestador.empresa,
        setor: prestador.setor || '',
        responsavel: getResponsavelSetor(prestador.setor) || '',
        objeto: prestador.objeto || '',
        aditivo: '',
        // Resetar critérios para valor padrão
        criterio1: 3,
        criterio2: 3,
        criterio3: 3,
        criterio4: 3,
        criterio5: 3,
        criterio6: 3,
        criterio7: 3,
        criterio8: 3,
        nota: 3,
        pontosPositivos: '',
        pontosNegativos: '',
        dataAvaliacao: new Date().toISOString().split('T')[0],
        pdfFile: null
      });
    } else {
      setForm({
        numeroContrato: '',
        empresa: '',
        setor: '',
        responsavel: '',
        objeto: '',
        aditivo: '',
        criterio1: 3,
        criterio2: 3,
        criterio3: 3,
        criterio4: 3,
        criterio5: 3,
        criterio6: 3,
        criterio7: 3,
        criterio8: 3,
        nota: 3,
        pontosPositivos: '',
        pontosNegativos: '',
        dataAvaliacao: new Date().toISOString().split('T')[0],
        pdfFile: null
      });
    }
    setModalOpen(true);
    setError('');
    setSuccess('');
    setCriteriosExtras([]); // Limpar critérios extras
  };

  // Deep-link a partir do módulo isolado do fiscal (alimenta este módulo)
  useEffect(() => {
    const num = location.state?.openAvaliacaoNumeroContrato;
    if (!num || deepLinkHandled.current || prestadores.length === 0) return;
    const p = prestadores.find((x) => String(x.numeroContrato) === String(num));
    if (!p) return;
    if (!canAddAvaliacao(p.setor)) {
      setError('Sem permissão para avaliar este contrato/setor.');
      deepLinkHandled.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    deepLinkHandled.current = true;
    handleOpenModal(p);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abre modal uma vez após carregar prestadores
  }, [prestadores, location.state?.openAvaliacaoNumeroContrato]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setForm({
      numeroContrato: '',
      empresa: '',
      setor: '',
      responsavel: '',
      objeto: '',
      aditivo: '',
      criterio1: 3,
      criterio2: 3,
      criterio3: 3,
      criterio4: 3,
      criterio5: 3,
      criterio6: 3,
      criterio7: 3,
      criterio8: 3,
      nota: 3,
      pontosPositivos: '',
      pontosNegativos: '',
      dataAvaliacao: new Date().toISOString().split('T')[0],
      pdfFile: null
    });
    setError('');
    setSuccess('');
    setAditivos([]);
    setCriteriosExtras([]); // Limpar critérios extras
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setForm({ ...form, pdfFile: file });
    } else {
      setError('Por favor, selecione apenas arquivos PDF');
    }
  };

  const handleSubmit = async () => {
    if (!form.numeroContrato || !form.empresa || !form.dataAvaliacao) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    // Calcular nota final automaticamente
    const notaFinal = calcularNotaFinal();

    const formData = new FormData();
    formData.append('numeroContrato', form.numeroContrato);
    formData.append('empresa', form.empresa);
    formData.append('setor', form.setor);
    formData.append('responsavel', form.responsavel);
    formData.append('objeto', form.objeto);
    formData.append('aditivo', form.aditivo);
    // Adicionar os 8 critérios
    formData.append('criterio1', form.criterio1);
    formData.append('criterio2', form.criterio2);
    formData.append('criterio3', form.criterio3);
    formData.append('criterio4', form.criterio4);
    formData.append('criterio5', form.criterio5);
    formData.append('criterio6', form.criterio6);
    formData.append('criterio7', form.criterio7);
    formData.append('criterio8', form.criterio8);
    formData.append('nota', notaFinal);
    formData.append('pontosPositivos', form.pontosPositivos);
    formData.append('pontosNegativos', form.pontosNegativos);
    formData.append('dataAvaliacao', form.dataAvaliacao);
    // Adicionar critérios extras
    formData.append('criteriosExtras', JSON.stringify(criteriosExtras));
    if (form.pdfFile) {
      formData.append('pdfFile', form.pdfFile);
    }

    try {
      console.log('Enviando avaliação:', {
        numeroContrato: form.numeroContrato,
        empresa: form.empresa,
        nota: notaFinal,
        criterios: {
          criterio1: form.criterio1,
          criterio2: form.criterio2,
          criterio3: form.criterio3,
          criterio4: form.criterio4,
          criterio5: form.criterio5,
          criterio6: form.criterio6,
          criterio7: form.criterio7,
          criterio8: form.criterio8
        },
        dataAvaliacao: form.dataAvaliacao
      });

      const response = await apiFetch('/api/avaliacoes', {
        method: 'POST',
        body: formData
      });

      console.log('Resposta do servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao salvar avaliação');
      }

      const result = await response.json();
      console.log('Avaliação salva com sucesso:', result);

      setSuccess('Avaliação salva com sucesso!');
      fetchAvaliacoes(); // Recarregar avaliações
      fetchPrestadores(); // Recarregar prestadores para atualizar data da última visita
      
      // Se a nota for menor que 3, mostrar opção de plano de ação
      if (notaFinal < 3) {
        setTimeout(() => {
          setSuccess('');
          if (window.confirm(`A nota final foi ${notaFinal} (${getClassificacaoNota(notaFinal).nivel}). Deseja criar um Plano de Ação para melhoria do prestador?`)) {
            // Calcular data de conclusão (90 dias depois)
            const dataInicio = new Date();
            const dataConclusao = new Date(dataInicio);
            dataConclusao.setDate(dataConclusao.getDate() + 90);
            
            setPlanoAcaoForm({
              avaliacaoId: result.id, // ID da avaliação criada
              numeroContrato: form.numeroContrato,
              empresa: form.empresa,
              dataInicio: dataInicio.toISOString().split('T')[0],
              dataConclusao: dataConclusao.toISOString().split('T')[0],
              problemasIdentificados: form.pontosNegativos || '',
              acoesCorretivas: '',
              responsavelAcompanhamento: form.responsavel || '',
              observacoes: `Nota obtida: ${notaFinal} (${getClassificacaoNota(notaFinal).nivel})`
            });
            setPlanoAcaoModalOpen(true);
          }
          handleCloseModal();
        }, 1500);
      } else {
        setTimeout(() => {
          handleCloseModal();
        }, 1500);
      }
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      setError(error.message);
    }
  };

  const handleViewPDF = async (avaliacao) => {
    if (!avaliacao.pdfPath) {
      setError('PDF não disponível');
      return;
    }
    try {
      if (pdfUrl && String(pdfUrl).startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
      const res = await apiFetch(`/api/avaliacoes/${avaliacao.id}/pdf`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Erro ao abrir PDF. Faça login novamente.');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
      setPdfTitle(`Avaliação - ${avaliacao.empresa}`);
      setPdfViewerOpen(true);
    } catch (e) {
      console.error('Erro ao abrir PDF da avaliação:', e);
      setError('Erro ao abrir PDF da avaliação');
    }
  };

  const handleClosePdfViewer = () => {
    if (pdfUrl && String(pdfUrl).startsWith('blob:')) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfViewerOpen(false);
    setPdfUrl('');
    setPdfTitle('');
  };

  const handleEditAvaliacao = (avaliacao) => {
    setEditingAvaliacao(avaliacao);
    
    // Buscar aditivos do prestador
    fetchAditivos(avaliacao.numeroContrato);
    
    setForm({
      numeroContrato: avaliacao.numeroContrato,
      empresa: avaliacao.empresa,
      setor: avaliacao.setor || '',
      responsavel: getResponsavelSetor(avaliacao.setor) || '',
      objeto: avaliacao.objeto || '',
      aditivo: avaliacao.aditivo || '',
      // Preencher os critérios da avaliação existente
      criterio1: avaliacao.criterio1 || 3,
      criterio2: avaliacao.criterio2 || 3,
      criterio3: avaliacao.criterio3 || 3,
      criterio4: avaliacao.criterio4 || 3,
      criterio5: avaliacao.criterio5 || 3,
      criterio6: avaliacao.criterio6 || 3,
      criterio7: avaliacao.criterio7 || 3,
      criterio8: avaliacao.criterio8 || 3,
      nota: avaliacao.nota,
      pontosPositivos: avaliacao.pontosPositivos || '',
      pontosNegativos: avaliacao.pontosNegativos || '',
      dataAvaliacao: avaliacao.dataAvaliacao,
      pdfFile: null
    });
    setEditModalOpen(true);
    setError('');
    setSuccess('');
    setCriteriosExtras([]); // Limpar critérios extras
  };

  const handleDeleteAvaliacao = (avaliacao) => {
    if (window.confirm(`Tem certeza que deseja excluir a avaliação de ${avaliacao.empresa}?`)) {
      apiFetch(`/api/avaliacoes/${avaliacao.id}`, {
        method: 'DELETE',
      })
      .then(res => {
        if (!res.ok) throw new Error('Erro ao excluir avaliação');
        setSuccess('Avaliação excluída com sucesso!');
        fetchAvaliacoes(); // Recarregar avaliações
      })
      .catch(err => {
        setError(err.message || 'Erro ao excluir avaliação');
      });
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingAvaliacao(null);
    setForm({
      numeroContrato: '',
      empresa: '',
      setor: '',
      responsavel: '',
      objeto: '',
      aditivo: '',
      criterio1: 3,
      criterio2: 3,
      criterio3: 3,
      criterio4: 3,
      criterio5: 3,
      criterio6: 3,
      criterio7: 3,
      criterio8: 3,
      nota: 3,
      pontosPositivos: '',
      pontosNegativos: '',
      dataAvaliacao: new Date().toISOString().split('T')[0],
      pdfFile: null
    });
    setError('');
    setSuccess('');
    setAditivos([]);
    setCriteriosExtras([]); // Limpar critérios extras
  };

  const handleUpdateAvaliacao = async () => {
    if (!form.numeroContrato || !form.empresa || !form.dataAvaliacao) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    // Calcular nota final automaticamente
    const notaFinal = calcularNotaFinal();

    const formData = new FormData();
    formData.append('numeroContrato', form.numeroContrato);
    formData.append('empresa', form.empresa);
    formData.append('setor', form.setor);
    formData.append('responsavel', form.responsavel);
    formData.append('objeto', form.objeto);
    formData.append('aditivo', form.aditivo);
    // Adicionar os 8 critérios
    formData.append('criterio1', form.criterio1);
    formData.append('criterio2', form.criterio2);
    formData.append('criterio3', form.criterio3);
    formData.append('criterio4', form.criterio4);
    formData.append('criterio5', form.criterio5);
    formData.append('criterio6', form.criterio6);
    formData.append('criterio7', form.criterio7);
    formData.append('criterio8', form.criterio8);
    formData.append('nota', notaFinal);
    formData.append('pontosPositivos', form.pontosPositivos);
    formData.append('pontosNegativos', form.pontosNegativos);
    formData.append('dataAvaliacao', form.dataAvaliacao);
    // Adicionar critérios extras
    formData.append('criteriosExtras', JSON.stringify(criteriosExtras));
    if (form.pdfFile) {
      formData.append('pdfFile', form.pdfFile);
    }

    try {
      const response = await apiFetch(`/api/avaliacoes/${editingAvaliacao.id}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao atualizar avaliação');
      }

      setSuccess('Avaliação atualizada com sucesso!');
      fetchAvaliacoes(); // Recarregar avaliações
      
      // Se a nota for menor que 3, mostrar opção de plano de ação
      if (notaFinal < 3) {
        setTimeout(() => {
          setSuccess('');
          if (window.confirm(`A nota final foi ${notaFinal} (${getClassificacaoNota(notaFinal).nivel}). Deseja criar um Plano de Ação para melhoria do prestador?`)) {
            // Calcular data de conclusão (90 dias depois)
            const dataInicio = new Date();
            const dataConclusao = new Date(dataInicio);
            dataConclusao.setDate(dataConclusao.getDate() + 90);
            
            setPlanoAcaoForm({
              avaliacaoId: editingAvaliacao.id,
              numeroContrato: form.numeroContrato,
              empresa: form.empresa,
              dataInicio: dataInicio.toISOString().split('T')[0],
              dataConclusao: dataConclusao.toISOString().split('T')[0],
              problemasIdentificados: form.pontosNegativos || '',
              acoesCorretivas: '',
              responsavelAcompanhamento: form.responsavel || '',
              observacoes: `Nota obtida: ${notaFinal} (${getClassificacaoNota(notaFinal).nivel})`
            });
            setPlanoAcaoModalOpen(true);
          }
          handleCloseEditModal();
        }, 1500);
      } else {
        setTimeout(() => {
          handleCloseEditModal();
        }, 1500);
      }
    } catch (error) {
      console.error('Erro ao atualizar avaliação:', error);
      setError(error.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <VisaoRestritaBanner />

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Cards de estatísticas */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main" fontWeight={700}>
                {avaliacoes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avaliações Realizadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main" fontWeight={700}>
                {prestadores.length - avaliacoes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pendentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main" fontWeight={700}>
                {prestadores.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Fornecedores
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ranking Top 5 Melhores Fornecedores */}
      <Card sx={{ 
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
        backdropFilter: 'blur(10px)',
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: '1px solid rgba(255,255,255,0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        },
        mb: 4
      }}>
        <CardContent sx={{ py: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center">
              <Box sx={{
                background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                borderRadius: '50%',
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(156,39,176,0.3)'
              }}>
                <BusinessIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{
                background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Top 5 Melhores Fornecedores
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AnalyticsIcon />}
              onClick={() => setIndicadoresModalOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                boxShadow: '0 4px 12px rgba(156,39,176,0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(156,39,176,0.4)'
                }
              }}
            >
              Indicador
            </Button>
          </Box>
          
          {(() => {
            // Calcular top fornecedores
            if (avaliacoes.length === 0 || prestadores.length === 0) {
              return (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhuma avaliação encontrada. Adicione avaliações para ver o ranking.
                  </Typography>
                </Box>
              );
            }

            // Agrupar avaliações por empresa e calcular média
            const avaliacoesPorEmpresa = {};
            avaliacoes.forEach(avaliacao => {
              if (!avaliacoesPorEmpresa[avaliacao.empresa]) {
                avaliacoesPorEmpresa[avaliacao.empresa] = [];
              }
              avaliacoesPorEmpresa[avaliacao.empresa].push(avaliacao.nota);
            });

            // Calcular média de cada empresa
            const empresasComMedia = Object.entries(avaliacoesPorEmpresa).map(([empresa, notas]) => {
              const media = notas.reduce((sum, nota) => sum + nota, 0) / notas.length;
              const prestador = prestadores.find(p => p.empresa === empresa);
              return {
                empresa,
                media: Math.round(media * 10) / 10, // Arredondar para 1 casa decimal
                contrato: prestador?.numeroContrato || 'N/A',
                setor: prestador?.setor || 'N/A',
                totalAvaliacoes: notas.length
              };
            });

            // Ordenar por média (decrescente) e pegar os 5 melhores
            const top5 = empresasComMedia
              .sort((a, b) => b.media - a.media)
              .slice(0, 5);

            return (
              <Grid container spacing={2}>
                {top5.map((fornecedor, index) => (
                  <Grid item xs={12} sm={6} md={2.4} key={index}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(240,248,255,0.6) 100%)',
                      borderRadius: 3,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Box sx={{
                          background: index === 0 ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' :
                                   index === 1 ? 'linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%)' :
                                   index === 2 ? 'linear-gradient(135deg, #CD7F32 0%, #B8860B 100%)' :
                                   'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                          borderRadius: '50%',
                          width: 50,
                          height: 50,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 1,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                          <Typography variant="h6" fontWeight={700} color="white">
                            {index + 1}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                          {fornecedor.empresa.length > 20 ? 
                            fornecedor.empresa.substring(0, 20) + '...' : 
                            fornecedor.empresa
                          }
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main" sx={{ mb: 0.5 }}>
                          {fornecedor.media}★
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fornecedor.totalAvaliacoes} avaliação{fornecedor.totalAvaliacoes > 1 ? 'ões' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {fornecedor.setor}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            );
          })()}
        </CardContent>
      </Card>

      {/* Barra de Filtros Avançados */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Lista de Fornecedores
          </Typography>
          {temFiltrosAtivos() && (
            <Chip 
              label={`${prestadoresFiltrados.length} de ${prestadores.length} resultados`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {temFiltrosAtivos() && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={limparFiltros}
              size="small"
            >
              Limpar Filtros
            </Button>
          )}
          <Button
            variant={temFiltrosAtivos() ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltroAvancadoOpen(true)}
            size="small"
          >
            Filtros Avançados
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nº Contrato</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Setor</TableCell>
                <TableCell>Fiscal / Responsável</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prestadoresFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {temFiltrosAtivos() ? 'Nenhum resultado encontrado com os filtros aplicados' : 'Nenhum prestador encontrado'}
                  </TableCell>
                </TableRow>
              ) : (
                prestadoresFiltrados.map((prestador) => {
                  const nomeFiscal = getResponsavelSetor(prestador.setor);
                  const fiscalClicavel = nomeFiscal && nomeFiscal !== 'Fiscal não cadastrado';
                  
                  return (
                    <TableRow key={prestador.id} hover>
                      <TableCell>{prestador.numeroContrato}</TableCell>
                      <TableCell>{prestador.empresa}</TableCell>
                      <TableCell>{prestador.cnpj}</TableCell>
                      <TableCell>{prestador.setor}</TableCell>
                      <TableCell>
                        {fiscalClicavel ? (
                          <Chip
                            icon={<PersonIcon />}
                            label={nomeFiscal}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() =>
                              handleOpenFiscalPanel(nomeFiscal, getPrestadoresPorFiscal(nomeFiscal))
                            }
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 2,
                              fontWeight: 600,
                              '&:hover': { bgcolor: 'primary.light', color: 'white', borderColor: 'primary.main' }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {nomeFiscal}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de Avaliação Estruturada */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Formulário de Qualificação e Avaliação do Fornecedor
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Cabeçalho com dados do prestador */}
          <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📋 Informações do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Setor"
                    value={form.setor}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Responsável"
                    value={form.responsavel}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Empresa"
                    value={form.empresa}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="N° do Contrato"
                    value={form.numeroContrato + (form.aditivo ? ` - ${form.aditivo}` : '')}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Objeto"
                    value={form.objeto}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                    multiline
                    rows={2}
                  />
                </Grid>
                {aditivos.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel>Selecionar Aditivo (Opcional)</InputLabel>
                      <Select
                        value={form.aditivo}
                        onChange={(e) => setForm({ ...form, aditivo: e.target.value })}
                        label="Selecionar Aditivo (Opcional)"
                      >
                        <MenuItem value="">Contrato Original</MenuItem>
                        {aditivos.map((aditivo) => (
                          <MenuItem key={aditivo.id} value={`${aditivo.numeroAditivo}º Termo Aditivo`}>
                            {aditivo.numeroAditivo}º Termo Aditivo
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Critérios de Avaliação */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                ⭐ Critérios de Avaliação do Serviço/Produto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Avalie cada critério de 1 (Ruim) a 5 (Excelente). A nota final será calculada automaticamente.
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { key: 'criterio1', label: 'Serviço prestado conforme o Contrato' },
                  { key: 'criterio2', label: 'Serviços prestados de acordo com a rotina/programação de execução' },
                  { key: 'criterio3', label: 'Qualidade na entrega do serviço/produto' },
                  { key: 'criterio4', label: 'Comunicação acessível e ágil' },
                  { key: 'criterio5', label: 'Empenho na resolução de problemas sinalizados na prestação do serviço' },
                  { key: 'criterio6', label: 'Confiabilidade e Entrega' },
                  { key: 'criterio7', label: 'Segurança na Execução do Serviço' },
                  { key: 'criterio8', label: 'Alinhamento estratégico de acordo com as diretrizes do hospital' }
                ].map((criterio, index) => (
                  <Grid item xs={12} key={criterio.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: '30px' }}>
                        {index + 1}.
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {criterio.label}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: '120px' }}>
                        <InputLabel>Nota</InputLabel>
                        <Select
                          value={form[criterio.key]}
                          onChange={(e) => setForm({ ...form, [criterio.key]: e.target.value })}
                          label="Nota"
                        >
                          <MenuItem value={1}>1 - Ruim</MenuItem>
                          <MenuItem value={2}>2 - Regular</MenuItem>
                          <MenuItem value={3}>3 - Bom</MenuItem>
                          <MenuItem value={4}>4 - Ótimo</MenuItem>
                          <MenuItem value={5}>5 - Excelente</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Critérios Extras */}
              {criteriosExtras.map((criterio, index) => (
                <Grid item xs={12} key={criterio.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #ff9800', borderRadius: 1, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: '30px' }}>
                      {9 + index}.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Digite o critério personalizado..."
                      value={criterio.descricao}
                      onChange={(e) => atualizarCriterioExtra(criterio.id, 'descricao', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: '120px' }}>
                      <InputLabel>Nota</InputLabel>
                      <Select
                        value={criterio.nota}
                        onChange={(e) => atualizarCriterioExtra(criterio.id, 'nota', e.target.value)}
                        label="Nota"
                      >
                        <MenuItem value={1}>1 - Ruim</MenuItem>
                        <MenuItem value={2}>2 - Regular</MenuItem>
                        <MenuItem value={3}>3 - Bom</MenuItem>
                        <MenuItem value={4}>4 - Ótimo</MenuItem>
                        <MenuItem value={5}>5 - Excelente</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removerCriterioExtra(criterio.id)}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {/* Botão para adicionar critério extra */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={adicionarCriterioExtra}
                    sx={{ borderStyle: 'dashed', borderColor: '#ff9800', color: '#ff9800' }}
                  >
                    Adicionar Critério Personalizado
                  </Button>
                </Box>
              </Grid>

              {/* Nota Final Calculada */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  📊 Nota Final Calculada
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {calcularNotaFinal()}
                </Typography>
                <Chip 
                  label={getClassificacaoNota(calcularNotaFinal()).nivel}
                  color={getClassificacaoNota(calcularNotaFinal()).cor}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Média dos {8 + criteriosExtras.length} critérios: ({[form.criterio1, form.criterio2, form.criterio3, form.criterio4, form.criterio5, form.criterio6, form.criterio7, form.criterio8].join(' + ')}{criteriosExtras.length > 0 ? ' + ' + criteriosExtras.map(c => c.nota || 3).join(' + ') : ''}) ÷ {8 + criteriosExtras.length} = {calcularNotaFinal()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Campos adicionais */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data de Avaliação"
                type="date"
                value={form.dataAvaliacao}
                onChange={(e) => setForm({ ...form, dataAvaliacao: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pontos Positivos / Elogios"
                value={form.pontosPositivos}
                onChange={(e) => setForm({ ...form, pontosPositivos: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Descreva os pontos positivos e elogios..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pontos Negativos / Pontos para Melhorar"
                value={form.pontosNegativos}
                onChange={(e) => setForm({ ...form, pontosNegativos: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Descreva os pontos negativos e sugestões de melhoria..."
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="pdf-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="pdf-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FileUploadIcon />}
                    fullWidth
                  >
                    Upload PDF da Avaliação (Opcional)
                  </Button>
                </label>
                {form.pdfFile && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileUploadIcon color="success" />
                    <Typography variant="body2">{form.pdfFile.name}</Typography>
                    <IconButton size="small" onClick={() => setForm({ ...form, pdfFile: null })}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: 2
            }}
          >
            Salvar Avaliação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal do Visualizador de PDF */}
      <Dialog 
        open={pdfViewerOpen} 
        onClose={handleClosePdfViewer} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {pdfTitle}
          <IconButton
            onClick={handleClosePdfViewer}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <iframe
            src={pdfUrl}
            width="100%"
            height="600px"
            style={{ border: 'none' }}
            title="PDF Viewer"
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Avaliação Estruturada */}
      <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <Typography variant="h6" fontWeight={600}>
            Editar Avaliação de Fornecedor
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Cabeçalho com dados do prestador */}
          <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📋 Informações do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Setor"
                    value={form.setor}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Responsável"
                    value={form.responsavel}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Empresa"
                    value={form.empresa}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="N° do Contrato"
                    value={form.numeroContrato + (form.aditivo ? ` - ${form.aditivo}` : '')}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Objeto"
                    value={form.objeto}
                    fullWidth
                    margin="dense"
                    disabled
                    sx={{ backgroundColor: '#f5f5f5' }}
                    multiline
                    rows={2}
                  />
                </Grid>
                {aditivos.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel>Selecionar Aditivo (Opcional)</InputLabel>
                      <Select
                        value={form.aditivo}
                        onChange={(e) => setForm({ ...form, aditivo: e.target.value })}
                        label="Selecionar Aditivo (Opcional)"
                      >
                        <MenuItem value="">Contrato Original</MenuItem>
                        {aditivos.map((aditivo) => (
                          <MenuItem key={aditivo.id} value={`${aditivo.numeroAditivo}º Termo Aditivo`}>
                            {aditivo.numeroAditivo}º Termo Aditivo
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Critérios de Avaliação */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                ⭐ Critérios de Avaliação do Serviço/Produto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Avalie cada critério de 1 (Ruim) a 5 (Excelente). A nota final será calculada automaticamente.
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { key: 'criterio1', label: 'Serviço prestado conforme o Contrato' },
                  { key: 'criterio2', label: 'Serviços prestados de acordo com a rotina/programação de execução' },
                  { key: 'criterio3', label: 'Qualidade na entrega do serviço/produto' },
                  { key: 'criterio4', label: 'Comunicação acessível e ágil' },
                  { key: 'criterio5', label: 'Empenho na resolução de problemas sinalizados na prestação do serviço' },
                  { key: 'criterio6', label: 'Confiabilidade e Entrega' },
                  { key: 'criterio7', label: 'Segurança na Execução do Serviço' },
                  { key: 'criterio8', label: 'Alinhamento estratégico de acordo com as diretrizes do hospital' }
                ].map((criterio, index) => (
                  <Grid item xs={12} key={criterio.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: '30px' }}>
                        {index + 1}.
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {criterio.label}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: '120px' }}>
                        <InputLabel>Nota</InputLabel>
                        <Select
                          value={form[criterio.key]}
                          onChange={(e) => setForm({ ...form, [criterio.key]: e.target.value })}
                          label="Nota"
                        >
                          <MenuItem value={1}>1 - Ruim</MenuItem>
                          <MenuItem value={2}>2 - Regular</MenuItem>
                          <MenuItem value={3}>3 - Bom</MenuItem>
                          <MenuItem value={4}>4 - Ótimo</MenuItem>
                          <MenuItem value={5}>5 - Excelente</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Critérios Extras */}
              {criteriosExtras.map((criterio, index) => (
                <Grid item xs={12} key={criterio.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #ff9800', borderRadius: 1, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: '30px' }}>
                      {9 + index}.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Digite o critério personalizado..."
                      value={criterio.descricao}
                      onChange={(e) => atualizarCriterioExtra(criterio.id, 'descricao', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: '120px' }}>
                      <InputLabel>Nota</InputLabel>
                      <Select
                        value={criterio.nota}
                        onChange={(e) => atualizarCriterioExtra(criterio.id, 'nota', e.target.value)}
                        label="Nota"
                      >
                        <MenuItem value={1}>1 - Ruim</MenuItem>
                        <MenuItem value={2}>2 - Regular</MenuItem>
                        <MenuItem value={3}>3 - Bom</MenuItem>
                        <MenuItem value={4}>4 - Ótimo</MenuItem>
                        <MenuItem value={5}>5 - Excelente</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removerCriterioExtra(criterio.id)}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {/* Botão para adicionar critério extra */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={adicionarCriterioExtra}
                    sx={{ borderStyle: 'dashed', borderColor: '#ff9800', color: '#ff9800' }}
                  >
                    Adicionar Critério Personalizado
                  </Button>
                </Box>
              </Grid>

              {/* Nota Final Calculada */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  📊 Nota Final Calculada
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {calcularNotaFinal()}
                </Typography>
                <Chip 
                  label={getClassificacaoNota(calcularNotaFinal()).nivel}
                  color={getClassificacaoNota(calcularNotaFinal()).cor}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Média dos {8 + criteriosExtras.length} critérios: ({[form.criterio1, form.criterio2, form.criterio3, form.criterio4, form.criterio5, form.criterio6, form.criterio7, form.criterio8].join(' + ')}{criteriosExtras.length > 0 ? ' + ' + criteriosExtras.map(c => c.nota || 3).join(' + ') : ''}) ÷ {8 + criteriosExtras.length} = {calcularNotaFinal()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Campos adicionais */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data de Avaliação"
                type="date"
                value={form.dataAvaliacao}
                onChange={(e) => setForm({ ...form, dataAvaliacao: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pontos Positivos / Elogios"
                value={form.pontosPositivos}
                onChange={(e) => setForm({ ...form, pontosPositivos: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Descreva os pontos positivos e elogios..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pontos Negativos / Pontos para Melhorar"
                value={form.pontosNegativos}
                onChange={(e) => setForm({ ...form, pontosNegativos: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Descreva os pontos negativos e sugestões de melhoria..."
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="pdf-upload-edit"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="pdf-upload-edit">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FileUploadIcon />}
                    fullWidth
                  >
                    Upload PDF da Avaliação (Opcional)
                  </Button>
                </label>
                {form.pdfFile && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileUploadIcon color="success" />
                    <Typography variant="body2">{form.pdfFile.name}</Typography>
                    <IconButton size="small" onClick={() => setForm({ ...form, pdfFile: null })}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditModal} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleUpdateAvaliacao} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: 2
            }}
          >
            Atualizar Avaliação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Indicadores */}
      <Dialog 
        open={indicadoresModalOpen} 
        onClose={() => setIndicadoresModalOpen(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' }
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <AnalyticsIcon />
          Dashboard de Indicadores - Avaliação de Fornecedores
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Abas de Navegação */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={indicadoresTab} 
              onChange={(e, newValue) => setIndicadoresTab(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }
              }}
            >
                    <Tab label="📊 Dashboard Estratégico" />
                    <Tab label="📋 Planos de Ação" />
                    <Tab label="📈 Dashboard de Avaliações" />
                    <Tab label="📋 Relatórios Gerenciais" />
            </Tabs>
          </Box>
          {/* Conteúdo da Aba 1: Dashboard Estratégico */}
          {indicadoresTab === 0 && (
            <DashboardInteligente data={prestadores} avaliacoes={avaliacoes} />
          )}

                {/* Conteúdo da Aba 2: Planos de Ação */}
                {indicadoresTab === 1 && (
                  <Box sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                      <Box>
                        <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
                          🚨 Planos de Ação Ativos
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Acompanhamento de fornecedores com notas abaixo de 3.0
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<TableChartIcon />}
                        onClick={exportarExcelAvaliacoes}
                        sx={{
                          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                          borderRadius: 2,
                          px: 3,
                          py: 1
                        }}
                      >
                        📊 Exportar Excel
                      </Button>
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }}>
                      📌 Os planos de ação são criados automaticamente quando uma avaliação tem nota final inferior a 3.0
                    </Alert>

              {planosAcao.length === 0 ? (
                <Card>
                  <CardContent>
                    <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
                      Nenhum plano de ação cadastrado no momento
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Grid container spacing={2}>
                  {planosAcao.map((plano) => {
                    const diasRestantes = Math.ceil((new Date(plano.dataConclusao) - new Date()) / (1000 * 60 * 60 * 24));
                    const statusCor = plano.status === 'ativo' ? 'warning' : plano.status === 'concluido' ? 'success' : 'error';
                    
                    return (
                      <Grid item xs={12} key={plano.id}>
                        <Card sx={{ 
                          border: `2px solid ${plano.status === 'ativo' ? '#ff9800' : '#4caf50'}`,
                          boxShadow: 3
                        }}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="h6" fontWeight={600}>
                                {plano.empresa}
                              </Typography>
                              <Chip 
                                label={plano.status === 'ativo' ? 'ATIVO' : 'CONCLUÍDO'} 
                                color={statusCor}
                                size="small"
                              />
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Contrato:</strong> {plano.numeroContrato}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Início:</strong> {formatDate(plano.dataInicio)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Conclusão:</strong> {formatDate(plano.dataConclusao)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Problemas:</strong> {plano.problemasIdentificados}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2">
                                  <strong>Ações Corretivas:</strong> {plano.acoesCorretivas}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2">
                                  <strong>Responsável:</strong> {plano.responsavelAcompanhamento}
                                </Typography>
                              </Grid>
                              {plano.observacoes && (
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Observações:</strong> {plano.observacoes}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>

                            {plano.status === 'ativo' && (
                              <Box mt={2}>
                                <Alert severity={diasRestantes <= 30 ? 'error' : 'warning'}>
                                  ⏰ Restam <strong>{diasRestantes}</strong> dias para conclusão
                                </Alert>
                              </Box>
                            )}

                            <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
                              {plano.status === 'ativo' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={async () => {
                                    if (window.confirm('Deseja marcar este plano de ação como concluído?')) {
                                      try {
                                        await apiFetch(`/api/planos-acao/${plano.id}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ ...plano, status: 'concluido' })
                                        });
                                        setSuccess('Plano de ação marcado como concluído!');
                                        fetchPlanosAcao();
                                        setTimeout(() => setSuccess(''), 3000);
                                      } catch (error) {
                                        setError('Erro ao atualizar plano de ação');
                                      }
                                    }
                                  }}
                                >
                                  Marcar como Concluído
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={async () => {
                                  if (window.confirm('Deseja excluir este plano de ação?')) {
                                    try {
                                      await apiFetch(`/api/planos-acao/${plano.id}`, {
                                        method: 'DELETE'
                                      });
                                      setSuccess('Plano de ação excluído!');
                                      fetchPlanosAcao();
                                      setTimeout(() => setSuccess(''), 3000);
                                    } catch (error) {
                                      setError('Erro ao excluir plano de ação');
                                    }
                                  }
                                }}
                              >
                                Excluir
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {/* Conteúdo da Aba 3: Dashboard de Avaliações */}
          {indicadoresTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
                📈 Dashboard de Avaliações
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Análise detalhada e histórico de avaliações
              </Typography>

              {(() => {
                const stats = calcularEstatisticasAvaliacoes();
                if (!stats) {
                  return (
                    <Card>
                      <CardContent>
                        <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
                          Nenhuma avaliação encontrada para análise
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <Grid container spacing={3}>
                    {/* Cards de Resumo */}
                    <Grid item xs={12} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700} color="primary">
                          {stats.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total de Avaliações
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {stats.mediaGeral}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Média Geral
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <BusinessIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700} color="info.main">
                          {Object.keys(stats.porEmpresa).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Empresas Avaliadas
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <BarChartIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {stats.distribuicao.excelente + stats.distribuicao.otimo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ótimas/Excelentes
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Distribuição por Classificação */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            📊 Distribuição por Classificação
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            {[
                              { key: 'excelente', label: 'Excelente (4.1-5.0)', cor: '#4caf50', valor: stats.distribuicao.excelente },
                              { key: 'otimo', label: 'Ótimo (3.1-4.0)', cor: '#2196f3', valor: stats.distribuicao.otimo },
                              { key: 'bom', label: 'Bom (2.1-3.0)', cor: '#ff9800', valor: stats.distribuicao.bom },
                              { key: 'regular', label: 'Regular (1.1-2.0)', cor: '#ff5722', valor: stats.distribuicao.regular },
                              { key: 'ruim', label: 'Ruim (0.0-1.0)', cor: '#f44336', valor: stats.distribuicao.ruim }
                            ].map((item) => {
                              const percentual = stats.total > 0 ? Math.round((item.valor / stats.total) * 100) : 0;
                              return (
                                <Box key={item.key} sx={{ mb: 2 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {item.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {item.valor} ({percentual}%)
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={percentual}
                                    sx={{
                                      height: 8,
                                      borderRadius: 4,
                                      backgroundColor: 'rgba(0,0,0,0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: item.cor,
                                        borderRadius: 4
                                      }
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Top Empresas */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            🏆 Top Empresas por Nota
                          </Typography>
                          <List dense>
                            {Object.entries(stats.porEmpresa)
                              .sort(([,a], [,b]) => b.media - a.media)
                              .slice(0, 5)
                              .map(([empresa, dados], index) => (
                                <ListItem key={empresa} sx={{ px: 0 }}>
                                  <ListItemIcon>
                                    <Chip 
                                      label={index + 1} 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                                        color: index < 3 ? 'white' : 'text.primary'
                                      }} 
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={empresa}
                                    secondary={`${dados.total} avaliação(ões)`}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="h6" 
                                      fontWeight={600}
                                      sx={{ color: obterCorPorNota(dados.media) }}
                                    >
                                      {dados.media}
                                    </Typography>
                                    <Chip
                                      label={obterClassificacaoPorNota(dados.media)}
                                      size="small"
                                      sx={{ 
                                        bgcolor: obterCorPorNota(dados.media),
                                        color: 'white',
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  </Box>
                                </ListItem>
                              ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Tendência Temporal */}
                    {stats.tendencia.length > 0 && (
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              📈 Tendência dos Últimos 6 Meses
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                              {stats.tendencia.map((mes, index) => (
                                <Grid item xs={6} sm={4} md={2} key={mes.mes}>
                                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {mes.mes}
                                    </Typography>
                                    <Typography 
                                      variant="h6" 
                                      fontWeight={600}
                                      sx={{ color: obterCorPorNota(mes.media) }}
                                    >
                                      {mes.media}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {mes.total} aval.
                                    </Typography>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Avaliações Recentes */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            🕒 Avaliações Recentes
                          </Typography>
                          <List>
                            {stats.recentes.map((avaliacao, index) => (
                              <React.Fragment key={avaliacao.id}>
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemIcon>
                                    <AssessmentIcon sx={{ color: obterCorPorNota(avaliacao.nota) }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={avaliacao.empresa}
                                    secondary={`${formatDate(avaliacao.dataAvaliacao)} - Contrato ${avaliacao.numeroContrato}`}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="h6" 
                                      fontWeight={600}
                                      sx={{ color: obterCorPorNota(avaliacao.nota) }}
                                    >
                                      {avaliacao.nota}
                                    </Typography>
                                    <Chip
                                      label={obterClassificacaoPorNota(avaliacao.nota)}
                                      size="small"
                                      sx={{ 
                                        bgcolor: obterCorPorNota(avaliacao.nota),
                                        color: 'white',
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  </Box>
                                </ListItem>
                                {index < stats.recentes.length - 1 && <Divider />}
                              </React.Fragment>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                );
              })()}
            </Box>
          )}

          {/* Conteúdo da Aba 4: Relatórios Gerenciais */}
          {indicadoresTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
                📋 Relatórios Gerenciais
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Relatórios executivos e operacionais personalizados
              </Typography>

              <Grid container spacing={3}>
                {/* Relatório Executivo */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: '2px solid #2196F3' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600} color="primary">
                            📊 Relatório Executivo
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Resumo para gestores e diretores
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Relatório resumido com indicadores-chave, tendências e recomendações estratégicas.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="KPIs Principais" size="small" color="primary" variant="outlined" />
                        <Chip label="Tendências" size="small" color="primary" variant="outlined" />
                        <Chip label="Recomendações" size="small" color="primary" variant="outlined" />
                      </Box>
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<PictureAsPdfIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                            borderRadius: 2
                          }}
                        >
                          Gerar Relatório Executivo
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Relatório Operacional */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: '2px solid #4CAF50' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <BarChartIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600} color="success.main">
                            🔧 Relatório Operacional
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Detalhado para fiscais e analistas
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Relatório detalhado com análises por critério, histórico completo e planos de ação.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Análise Detalhada" size="small" color="success" variant="outlined" />
                        <Chip label="Histórico" size="small" color="success" variant="outlined" />
                        <Chip label="Planos de Ação" size="small" color="success" variant="outlined" />
                      </Box>
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<TableChartIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                            borderRadius: 2
                          }}
                        >
                          Gerar Relatório Operacional
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Relatório de Conformidade */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: '2px solid #FF9800' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600} color="warning.main">
                            ⚠️ Relatório de Conformidade
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Foco em planos de ação e melhorias
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Relatório focado em não-conformidades, planos de ação ativos e status de melhorias.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Não-conformidades" size="small" color="warning" variant="outlined" />
                        <Chip label="Planos Ativos" size="small" color="warning" variant="outlined" />
                        <Chip label="Status" size="small" color="warning" variant="outlined" />
                      </Box>
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<WarningIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                            borderRadius: 2
                          }}
                        >
                          Gerar Relatório de Conformidade
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Relatório de Tendências */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: '2px solid #9C27B0' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <TrendingUpIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600} color="secondary.main">
                            📈 Relatório de Tendências
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Análise temporal e projeções
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Relatório com análise temporal, tendências de melhoria e projeções futuras.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Análise Temporal" size="small" color="secondary" variant="outlined" />
                        <Chip label="Tendências" size="small" color="secondary" variant="outlined" />
                        <Chip label="Projeções" size="small" color="secondary" variant="outlined" />
                      </Box>
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<TrendingUpIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                            borderRadius: 2
                          }}
                        >
                          Gerar Relatório de Tendências
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Filtros Avançados */}
                <Grid item xs={12}>
                  <Card sx={{ border: '2px solid #607D8B' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontWeight={600} color="info.main">
                        🔍 Filtros Avançados
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Personalize os relatórios com filtros específicos
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            label="Data Início"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            label="Data Fim"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Empresa</InputLabel>
                            <Select label="Empresa">
                              <MenuItem value="">Todas</MenuItem>
                              {[...new Set(prestadores.map(p => p.empresa))].map(empresa => (
                                <MenuItem key={empresa} value={empresa}>{empresa}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Setor</InputLabel>
                            <Select label="Setor">
                              <MenuItem value="">Todos</MenuItem>
                              {[...new Set(prestadores.map(p => p.setor).filter(Boolean))].map(setor => (
                                <MenuItem key={setor} value={setor}>{setor}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Faixa de Nota</InputLabel>
                            <Select label="Faixa de Nota">
                              <MenuItem value="">Todas</MenuItem>
                              <MenuItem value="excelente">Excelente (4.1-5.0)</MenuItem>
                              <MenuItem value="otimo">Ótimo (3.1-4.0)</MenuItem>
                              <MenuItem value="bom">Bom (2.1-3.0)</MenuItem>
                              <MenuItem value="regular">Regular (1.1-2.0)</MenuItem>
                              <MenuItem value="ruim">Ruim (0.0-1.0)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status Plano de Ação</InputLabel>
                            <Select label="Status Plano de Ação">
                              <MenuItem value="">Todos</MenuItem>
                              <MenuItem value="ativo">Ativo</MenuItem>
                              <MenuItem value="concluido">Concluído</MenuItem>
                              <MenuItem value="vencido">Vencido</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box display="flex" gap={1} mt={2}>
                            <Button
                              variant="outlined"
                              startIcon={<AssessmentIcon />}
                              size="small"
                            >
                              Aplicar Filtros
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                            >
                              Limpar
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setIndicadoresModalOpen(false)} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
              borderRadius: 2
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Plano de Ação */}
      <Dialog open={planoAcaoModalOpen} onClose={() => setPlanoAcaoModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #ff5722 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <Typography variant="h6" fontWeight={600}>
            🚨 Plano de Ação - Melhoria do Prestador
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Atenção: Nota abaixo do esperado!
            </Typography>
            <Typography>
              É necessário criar um plano de ação de 90 dias para melhorar a qualidade dos serviços prestados.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Empresa"
                value={planoAcaoForm.empresa}
                fullWidth
                margin="normal"
                disabled
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data de Início do Plano"
                type="date"
                value={planoAcaoForm.dataInicio}
                onChange={(e) => setPlanoAcaoForm({ ...planoAcaoForm, dataInicio: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data de Conclusão (90 dias)"
                type="date"
                value={planoAcaoForm.dataConclusao}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Problemas Identificados"
                value={planoAcaoForm.problemasIdentificados}
                onChange={(e) => setPlanoAcaoForm({ ...planoAcaoForm, problemasIdentificados: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Descreva os principais problemas identificados na avaliação..."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ações Corretivas Propostas"
                value={planoAcaoForm.acoesCorretivas}
                onChange={(e) => setPlanoAcaoForm({ ...planoAcaoForm, acoesCorretivas: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={4}
                placeholder="Descreva as ações que devem ser tomadas para resolver os problemas..."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Responsável pelo Acompanhamento"
                value={planoAcaoForm.responsavelAcompanhamento}
                onChange={(e) => setPlanoAcaoForm({ ...planoAcaoForm, responsavelAcompanhamento: e.target.value })}
                fullWidth
                margin="normal"
                placeholder="Nome do responsável por acompanhar a execução do plano..."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observações Adicionais"
                value={planoAcaoForm.observacoes}
                onChange={(e) => setPlanoAcaoForm({ ...planoAcaoForm, observacoes: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                placeholder="Observações ou comentários adicionais..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPlanoAcaoModalOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ff5722 100%)',
              borderRadius: 2
            }}
            onClick={async () => {
              // Validação
              if (!planoAcaoForm.problemasIdentificados || !planoAcaoForm.acoesCorretivas || !planoAcaoForm.responsavelAcompanhamento) {
                setError('Preencha todos os campos obrigatórios do plano de ação');
                return;
              }

              try {
                const response = await apiFetch('/api/planos-acao', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(planoAcaoForm)
                });

                if (!response.ok) {
                  throw new Error('Erro ao salvar plano de ação');
                }

                setSuccess('Plano de ação criado com sucesso!');
                fetchPlanosAcao(); // Recarregar planos
                setPlanoAcaoModalOpen(false);
                
                setTimeout(() => {
                  setSuccess('');
                }, 3000);
              } catch (error) {
                console.error('Erro ao salvar plano de ação:', error);
                setError('Erro ao salvar plano de ação');
              }
            }}
          >
            Salvar Plano de Ação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Filtros Avançados */}
      <Dialog 
        open={filtroAvancadoOpen} 
        onClose={() => setFiltroAvancadoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <FilterListIcon />
          Filtros Avançados
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Filtro por Setor */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={filtros.setor}
                  onChange={(e) => setFiltros({...filtros, setor: e.target.value})}
                  label="Setor"
                >
                  <MenuItem value="">Todos os Setores</MenuItem>
                  {[...new Set(prestadores.map(p => p.setor).filter(Boolean))].map(setor => (
                    <MenuItem key={setor} value={setor}>{setor}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro por Status de Avaliação */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status de Avaliação</InputLabel>
                <Select
                  value={filtros.statusAvaliacao}
                  onChange={(e) => setFiltros({...filtros, statusAvaliacao: e.target.value})}
                  label="Status de Avaliação"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="avaliados">✅ Avaliados</MenuItem>
                  <MenuItem value="naoAvaliados">❌ Não Avaliados</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro por Empresa */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome da Empresa"
                value={filtros.empresa}
                onChange={(e) => setFiltros({...filtros, empresa: e.target.value})}
                fullWidth
                placeholder="Digite o nome da empresa..."
              />
            </Grid>

            {/* Filtro por Número do Contrato */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Número do Contrato"
                value={filtros.numeroContrato}
                onChange={(e) => setFiltros({...filtros, numeroContrato: e.target.value})}
                fullWidth
                placeholder="Digite o número do contrato..."
              />
            </Grid>

            {/* Filtro por Data de Assinatura */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                📅 Data de Assinatura do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Inicial (ex: 2024 ou 2024-01 ou 2024-01-15)"
                    value={filtros.assinaturaInicio}
                    onChange={(e) => setFiltros({...filtros, assinaturaInicio: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-01) ou data completa"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Final (ex: 2024 ou 2024-12 ou 2024-12-31)"
                    value={filtros.assinaturaFim}
                    onChange={(e) => setFiltros({...filtros, assinaturaFim: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-12) ou data completa"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Filtro por Início da Vigência */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                📆 Início da Vigência
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Inicial (ex: 2024 ou 2024-01 ou 2024-01-15)"
                    value={filtros.vigenciaInicioInicio}
                    onChange={(e) => setFiltros({...filtros, vigenciaInicioInicio: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-01) ou data completa"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Final (ex: 2024 ou 2024-12 ou 2024-12-31)"
                    value={filtros.vigenciaInicioFim}
                    onChange={(e) => setFiltros({...filtros, vigenciaInicioFim: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-12) ou data completa"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Filtro por Fim da Vigência */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                📅 Fim da Vigência
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Inicial (ex: 2024 ou 2024-01 ou 2024-01-15)"
                    value={filtros.vigenciaFimInicio}
                    onChange={(e) => setFiltros({...filtros, vigenciaFimInicio: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-01) ou data completa"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Final (ex: 2024 ou 2024-12 ou 2024-12-31)"
                    value={filtros.vigenciaFimFim}
                    onChange={(e) => setFiltros({...filtros, vigenciaFimFim: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-12) ou data completa"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Resumo dos Resultados */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: 2, 
                textAlign: 'center' 
              }}>
                <Typography variant="body2" color="text.secondary">
                  {temFiltrosAtivos() ? (
                    <>
                      <strong>{prestadoresFiltrados.length}</strong> de <strong>{prestadores.length}</strong> fornecedores encontrados
                    </>
                  ) : (
                    <>
                      Mostrando todos os <strong>{prestadores.length}</strong> fornecedores
                    </>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setFiltroAvancadoOpen(false)} 
            color="inherit"
          >
            Fechar
          </Button>
          <Button 
            onClick={limparFiltros}
            variant="outlined"
            startIcon={<ClearIcon />}
          >
            Limpar Filtros
          </Button>
          <Button 
            onClick={() => setFiltroAvancadoOpen(false)}
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: 2
            }}
          >
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>

      <DrawerPainelFiscal
        fiscal={fiscalPanelData.fiscal}
        prestadoresDoFiscal={fiscalPanelData.prestadores}
        open={fiscalPanelOpen}
        onClose={() => setFiscalPanelOpen(false)}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getNotaColor={getNotaColor}
        calcularDiasVencimento={calcularDiasVencimento}
        calcularNovaVisita={calcularNovaVisita}
        getAvaliacoesContrato={getAvaliacoesContrato}
        canAddAvaliacao={canAddAvaliacao}
        isCargoCorporativo={isCargoCorporativo}
        onAvaliar={handleOpenModal}
        onEditarAvaliacao={handleEditAvaliacao}
        onExcluirAvaliacao={handleDeleteAvaliacao}
        onGerarPdf={gerarPDFAvaliacao}
        onVerPdf={handleViewPDF}
      />
    </Box>
  );
} 
